import { Upload, DetailedError } from "tus-js-client";
import { isNumber } from "lodash-es";

import { GLOBAL_API_CLIENT } from "@/modules/api/globalApiClient";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { logger } from "@/modules/logger";
import type { UploadImportFileParams, ImportFileUploadResult } from "./types";
import { generateImportTusFingerprint, constructBlobName } from "./utils";

const TUS_ENDPOINT = "https://storage-api.mem.ai/files";
const TUS_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const TUS_RETRY_DELAYS = [0, 1000, 3000, 7000, 15000];

export async function uploadImportFile(
  params: UploadImportFileParams
): Promise<ImportFileUploadResult> {
  const { blob, fileId, filename, mimeType, config, onProgress, signal } = params;

  let normalizedFilename: string;
  try {
    normalizedFilename = normalizeFilename({ fileName: filename });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Invalid filename";
    logger.error({
      message: "[import-tus-service] filename normalization failed",
      info: { fileId, filename, error: errorMessage },
    });
    return { success: false, error: errorMessage };
  }

  const authToken = GLOBAL_API_CLIENT.getAuthToken();
  if (!authToken) {
    logger.error({
      message: "[import-tus-service] no auth token available",
      info: { fileId },
    });
    return { success: false, error: "No auth token available" };
  }

  const blobName = constructBlobName({
    spaceAccountId: config.spaceAccountId,
    fileId,
    filename: normalizedFilename,
  });

  logger.info({
    message: "[import-tus-service] upload start",
    info: {
      fileId,
      filename: normalizedFilename,
      mimeType,
      sizeBytes: blob.size,
      blobName,
    },
  });

  const uploadStartTime = Date.now();

  return new Promise(resolve => {
    const upload = new Upload(blob, {
      endpoint: TUS_ENDPOINT,
      uploadSize: blob.size,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: TUS_RETRY_DELAYS,
      removeFingerprintOnSuccess: true,
      fingerprint: async (file: File, options: InstanceType<typeof Upload>["options"]) => {
        const fileSize = (() => {
          if (!isNumber(file.size)) {
            return "unknown";
          }
          return file.size;
        })();
        const endpoint = (() => {
          if (!options.endpoint) {
            return "unknown";
          }
          return options.endpoint;
        })();

        return generateImportTusFingerprint({
          fileId,
          filename: normalizedFilename,
          fileSize,
          endpoint,
        });
      },
      metadata: {
        filename: normalizedFilename,
        filetype: mimeType,
        space_account_id: config.spaceAccountId,
        file_id: fileId,
      },
      headers: {
        Authorization: `OAuthAccessToken ${authToken}`,
      },
      onProgress: (bytesSent: number, bytesTotal: number) => {
        if (onProgress && bytesTotal > 0) {
          const percent = Math.floor((bytesSent / bytesTotal) * 100);
          onProgress(percent);
        }
      },
      onSuccess: () => {
        logger.info({
          message: "[import-tus-service] upload complete",
          info: {
            fileId,
            filename: normalizedFilename,
            blobName,
            durationMs: Date.now() - uploadStartTime,
          },
        });
        resolve({ success: true, blobName });
      },
      onError: (error: Error | DetailedError) => {
        const errorMessage = error.message ?? "TUS upload failed";
        logger.error({
          message: "[import-tus-service] upload failed",
          info: {
            fileId,
            filename: normalizedFilename,
            error: errorMessage,
            durationMs: Date.now() - uploadStartTime,
          },
        });
        resolve({ success: false, error: errorMessage });
      },
    });

    if (signal) {
      if (signal.aborted) {
        resolve({ success: false, error: "Upload aborted" });
        return;
      }
      signal.addEventListener("abort", () => {
        upload.abort(true);
        resolve({ success: false, error: "Upload aborted" });
      });
    }

    upload.start();
  });
}
